import type { ManifestPage, Project, RevisionManifest } from '@p2b/contract';
import type { PublicationView } from '@p2b/sdk';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { ErrorNote } from '../../App.js';
import { api } from '../../api.js';
import { fill, formatBytes, formatDate, formatDay, useI18n } from '../../i18n.js';
import { Icon } from '../../ui.js';
import {
  ChecksTag,
  Dialog,
  ModelStill,
  MoreMenu,
  deliveryRows,
  useDeliver,
  useDeliveries,
  type Checks,
  type DeliveryRow,
} from './shared.js';

/**
 * Deliveries: the newest delivery in force as the large card; then, for clients, the earlier ones, and for staff
 * every frozen state of the project with its checks, changes and status (frozen, delivered, withdrawn).
 */
export function DeliveriesTab({ project }: { project: Project }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { pubs, revs, staff, manage, active, latest } = useDeliveries(project);
  const [toWithdraw, setToWithdraw] = useState<PublicationView | null>(null);
  const withdraw = useMutation({
    mutationFn: (id: string) => api.withdraw(project.id, id),
    onSuccess: () => {
      setToWithdraw(null);
      void qc.invalidateQueries({ queryKey: ['publications', project.id] });
    },
  });
  const onWithdraw = manage ? setToWithdraw : undefined;
  const rows = useMemo(() => deliveryRows(revs.data ?? [], pubs.data ?? []), [revs.data, pubs.data]);
  const earlier = active.slice(1);

  return (
    <div className="deliveries">
      <ErrorNote error={pubs.error ?? revs.error} />
      {pubs.isPending && <p className="muted">{t('loading')}</p>}
      {pubs.data && !latest && <p className="empty">{t('noDeliveries')}</p>}
      {latest && <DeliveryCard project={project} pub={latest} featured onWithdraw={onWithdraw} />}
      {latest && <p className="basis">{t('basisNote')}</p>}
      {!staff && earlier.length > 0 && (
        <section className="section">
          <h2 className="section-title">{t('earlierDeliveries')}</h2>
          <div className="delivery-list">
            {earlier.map((p) => (
              <DeliveryCard key={p.id} project={project} pub={p} />
            ))}
          </div>
        </section>
      )}
      {staff && (
        <section className="section">
          <h2 className="section-title">{t('allDeliveries')}</h2>
          {revs.data?.length === 0 ? (
            <p className="empty">{t('noFrozen')}</p>
          ) : (
            <DeliveryTable project={project} rows={rows} manage={manage} onWithdraw={setToWithdraw} />
          )}
        </section>
      )}
      {staff && <DraftPages project={project} />}
      {toWithdraw && (
        <WithdrawDialog
          pub={toWithdraw}
          pending={withdraw.isPending}
          error={withdraw.error}
          onConfirm={() => withdraw.mutate(toWithdraw.id)}
          onClose={() => setToWithdraw(null)}
        />
      )}
    </div>
  );
}

function DeliveryCard({
  project,
  pub,
  featured = false,
  onWithdraw,
}: {
  project: Project;
  pub: PublicationView;
  featured?: boolean;
  onWithdraw?: ((p: PublicationView) => void) | undefined;
}) {
  const { t, lang } = useI18n();
  const Title = featured ? 'h2' : 'h3';
  const pages = pub.pages ?? [];
  return (
    <article className={`delivery${featured ? ' featured' : ''}`}>
      {featured &&
        (pub.viewerUrl ? (
          <a className="delivery-visual" href={pub.viewerUrl} target="_blank" rel="noreferrer">
            <ModelStill src={pub.stillUrl} />
            <span className="delivery-hint">
              <Icon.Model width={15} height={15} />
              {t('openInBrowser')}
            </span>
          </a>
        ) : (
          <div className="delivery-visual">
            <ModelStill src={pub.stillUrl} />
          </div>
        ))}
      <div className="delivery-body">
        <div className="delivery-top">
          <div className="delivery-text">
            <Title className="delivery-title">{pub.title || pub.revisionLabel}</Title>
            {pub.notes && <p className="delivery-notes">{pub.notes}</p>}
          </div>
          {onWithdraw && (
            <MoreMenu>
              <button className="menu-item danger" role="menuitem" onClick={() => onWithdraw(pub)}>
                <Icon.Undo width={16} height={16} />
                {t('withdrawAction')}
              </button>
            </MoreMenu>
          )}
        </div>
        <dl className="titleblock">
          <div>
            <dt>{t('delivery')}</dt>
            <dd>{pub.revisionLabel}</dd>
          </div>
          <div>
            <dt>{t('delivered')}</dt>
            <dd>{formatDay(pub.publishedAt, lang)}</dd>
          </div>
        </dl>
        {pub.viewerUrl && (
          <div>
            <a
              className={`button open-model${featured ? ' primary lg' : ''}`}
              href={pub.viewerUrl}
              target="_blank"
              rel="noreferrer"
            >
              <Icon.Model />
              {t('openViewer')}
            </a>
          </div>
        )}
        {pages.length > 0 && (
          <div>
            <div className="group-label">{t('reports')}</div>
            <ul className="link-list">
              {pages.map((pg) => (
                <li key={pg.id}>
                  <Link to={new URL(pg.url).pathname}>
                    <Icon.Report width={17} height={17} />
                    <span className="link-name">{pg.title}</span>
                    <Icon.Chevron className="faint" width={15} height={15} />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
        {pub.downloads.length > 0 && (
          <div>
            <div className="group-label">{t('downloads')}</div>
            <ul className="link-list">
              {pub.downloads.map((d) => (
                <li key={d.path}>
                  <a href={api.publicationDownloadHref(project.id, pub.id, d.path)}>
                    <Icon.File width={17} height={17} />
                    <span className="link-name">{d.path.slice(d.path.lastIndexOf('/') + 1)}</span>
                    <span className="link-size">{formatBytes(d.size, lang)}</span>
                    <Icon.Download width={16} height={16} aria-label={t('download')} />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </article>
  );
}

/** Every frozen state of the project, newest first, with what changed since the one before. */
function DeliveryTable({
  project,
  rows,
  manage,
  onWithdraw,
}: {
  project: Project;
  rows: DeliveryRow[];
  manage: boolean;
  onWithdraw(p: PublicationView): void;
}) {
  const { t, lang } = useI18n();
  const deliver = useDeliver();
  const [open, setOpen] = useState<string | null>(null);
  return (
    <div className="sheet delivery-table">
      <table>
        <thead>
          <tr>
            <th className="caret-col" />
            <th>{t('delivery')}</th>
            <th className="hide-sm">{t('frozen')}</th>
            <th>{t('checks')}</th>
            <th className="hide-sm">{t('sincePrevious')}</th>
            <th className="num hide-sm">{t('files')}</th>
            <th>{t('status')}</th>
            <th className="num" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const { rev, state, active } = row;
            const m = rev.manifest as { fileCount?: number; checks?: Checks | null };
            const isOpen = open === rev.id;
            const c = rev.changes;
            const toggle = () => setOpen(isOpen ? null : rev.id);
            return (
              <Fragment key={rev.id}>
                <tr className={`clickable${isOpen ? ' open' : ''}`} onClick={toggle}>
                  <td className="caret-col">
                    <button
                      className="icon-button"
                      aria-expanded={isOpen}
                      aria-label={rev.label}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggle();
                      }}
                    >
                      <Icon.Chevron className={`caret${isOpen ? ' open' : ''}`} width={15} height={15} />
                    </button>
                  </td>
                  <td>
                    <div className="cell-strong">{rev.label}</div>
                    {active[0]?.title && <div className="muted small">{active[0].title}</div>}
                  </td>
                  <td className="hide-sm nowrap">
                    {formatDate(rev.createdAt, lang)}
                    {rev.createdByName && <div className="faint small">{rev.createdByName}</div>}
                  </td>
                  <td>
                    <ChecksTag checks={m.checks} />
                  </td>
                  <td className="hide-sm">
                    {c &&
                      (c.previous ? (
                        <Delta added={c.added} changed={c.changed} removed={c.removed} />
                      ) : (
                        <span className="faint">{t('firstFrozen')}</span>
                      ))}
                  </td>
                  <td className="num hide-sm">{m.fileCount ?? ''}</td>
                  <td>
                    <StateTag row={row} />
                  </td>
                  <td className="num actions" onClick={(e) => e.stopPropagation()}>
                    {manage && state !== 'delivered' && (
                      <button className="sm" onClick={() => deliver(rev.id)}>
                        <Icon.Send width={16} height={16} />
                        {t('deliver')}
                      </button>
                    )}
                    {state === 'delivered' && (active[0]?.viewerUrl || manage) && (
                      <MoreMenu>
                        {active[0]?.viewerUrl && (
                          <a
                            className="menu-item"
                            role="menuitem"
                            href={active[0].viewerUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <Icon.Model width={16} height={16} />
                            {t('openViewer')}
                          </a>
                        )}
                        {manage &&
                          active.map((p) => (
                            <button
                              key={p.id}
                              className="menu-item danger"
                              role="menuitem"
                              onClick={() => onWithdraw(p)}
                            >
                              <Icon.Undo width={16} height={16} />
                              {active.length > 1
                                ? `${t('withdraw')}: ${p.title || p.revisionLabel}`
                                : t('withdrawAction')}
                            </button>
                          ))}
                      </MoreMenu>
                    )}
                  </td>
                </tr>
                {isOpen && (
                  <tr className="expanded">
                    <td />
                    <td colSpan={7}>
                      <DeliveryDetail project={project} row={row} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Delta({ added, changed, removed }: { added: number; changed: number; removed: number }) {
  const { t } = useI18n();
  return (
    <span
      className="delta"
      title={`${t('added')} ${added}, ${t('changed')} ${changed}, ${t('removed')} ${removed}`}
    >
      <span className="delta-add">+{added}</span>
      <span className="delta-change">~{changed}</span>
      <span className="delta-remove">−{removed}</span>
    </span>
  );
}

function StateTag({ row }: { row: DeliveryRow }) {
  const { t, lang } = useI18n();
  if (row.state === 'delivered')
    return (
      <span className="tag ok nowrap">
        {t('delivered')} {formatDay(row.active[0]!.publishedAt, lang).replace(/ \d{4}$/, '')}
      </span>
    );
  if (row.state === 'withdrawn') return <span className="tag">{t('withdrawnState')}</span>;
  return <span className="tag warn">{t('frozen')}</span>;
}

const PATHS_SHOWN = 30;

function DeliveryDetail({ project, row }: { project: Project; row: DeliveryRow }) {
  const { t, lang } = useI18n();
  const rev = useQuery({
    queryKey: ['revision', project.id, row.rev.id],
    queryFn: () => api.revision(project.id, row.rev.id),
  });
  if (rev.isPending) return <p className="muted">{t('loading')}</p>;
  if (rev.error) return <ErrorNote error={rev.error} />;
  const m = rev.data.manifest as Partial<RevisionManifest>;
  const files = rev.data.changes?.files;
  const history = [...row.active, ...row.withdrawn].sort((a, b) =>
    b.publishedAt.localeCompare(a.publishedAt),
  );
  return (
    <div className="delivery-detail">
      <dl className="facts">
        {m.basis && (
          <>
            <dt>{t('basis')}</dt>
            <dd>{m.basis}</dd>
          </>
        )}
        {m.notes && (
          <>
            <dt>{t('notes')}</dt>
            <dd>{m.notes}</dd>
          </>
        )}
        {m.model?.checks?.log && (
          <>
            <dt>{t('checksLog')}</dt>
            <dd className="break mono small">{m.model.checks.log}</dd>
          </>
        )}
        {rev.data.createdByName && (
          <>
            <dt>{t('frozenBy')}</dt>
            <dd>
              {rev.data.createdByName}, {formatDate(rev.data.createdAt, lang)}
            </dd>
          </>
        )}
        {history.map((p) => (
          <Fragment key={p.id}>
            <dt>{p.withdrawnAt ? t('withdrawnState') : t('delivered')}</dt>
            <dd>
              {p.title || p.revisionLabel}, {formatDay(p.publishedAt, lang)}
              {p.withdrawnAt && <span className="muted"> → {formatDay(p.withdrawnAt, lang)}</span>}
            </dd>
          </Fragment>
        ))}
      </dl>
      {files && (
        <div className="changes">
          {(
            [
              ['added', files.added],
              ['changed', files.changed],
              ['removed', files.removed],
            ] as const
          ).map(([k, list]) => (
            <div key={k} className={`changes-col ${k}`}>
              <h4>
                {t(k)} ({list.length})
              </h4>
              <ul>
                {list.slice(0, PATHS_SHOWN).map((path) => (
                  <li key={path} className="break">
                    {path}
                  </li>
                ))}
                {list.length > PATHS_SHOWN && (
                  <li className="faint">{fill(t('andMorePaths'), { n: list.length - PATHS_SHOWN })}</li>
                )}
                {list.length === 0 && <li className="faint">—</li>}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WithdrawDialog({
  pub,
  pending,
  error,
  onConfirm,
  onClose,
}: {
  pub: PublicationView;
  pending: boolean;
  error: unknown;
  onConfirm(): void;
  onClose(): void;
}) {
  const { t } = useI18n();
  return (
    <Dialog
      title={t('withdrawTitle')}
      role="alertdialog"
      onClose={onClose}
      footer={
        <>
          <button className="quiet" onClick={onClose}>
            {t('cancel')}
          </button>
          <button className="danger-solid" onClick={onConfirm} disabled={pending}>
            {t('withdraw')}
          </button>
        </>
      }
    >
      <div className="stack-tight">
        <p>
          {fill(t('withdrawText'), { title: `${pub.title || pub.revisionLabel} (${pub.revisionLabel})` })}
        </p>
        <p className="muted small">{t('withdrawKeep')}</p>
        <ErrorNote error={error} />
      </div>
    </Dialog>
  );
}

/**
 * Deliver a frozen state to the client, like a transmittal: the form on the left, and on the right exactly what
 * the client will get (the model, the reports picked, the downloads) and who will see it.
 */
export function DeliverDialog({
  project,
  initial,
  onClose,
}: {
  project: Project;
  initial?: string | undefined;
  onClose(): void;
}) {
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const { pubs, revs, latest } = useDeliveries(project);
  const access = useQuery({ queryKey: ['access', project.id], queryFn: () => api.access(project.id) });
  const activeRevs = new Set((pubs.data ?? []).filter((p) => !p.withdrawnAt).map((p) => p.revisionId));
  const [form, setForm] = useState({ revision: initial ?? '', title: '', notes: '' });
  useEffect(() => {
    // Default: the newest frozen state not yet delivered, else the newest.
    if (form.revision || !revs.data?.length) return;
    const pick = revs.data.find((r) => !activeRevs.has(r.id)) ?? revs.data[0]!;
    setForm((f) => ({ ...f, revision: pick.id }));
  }, [revs.data]);
  const rev = useQuery({
    queryKey: ['revision', project.id, form.revision],
    queryFn: () => api.revision(project.id, form.revision),
    enabled: !!form.revision,
  });
  const m = rev.data?.manifest as Partial<RevisionManifest> | undefined;
  const revPages: ManifestPage[] = m?.pages ?? [];
  const [pages, setPages] = useState<string[]>([]);
  useEffect(() => {
    setPages(revPages.filter((p) => p.audience === 'client').map((p) => p.id));
    // Reset the choice when another revision is picked.
  }, [rev.data]);
  const sizes = new Map((m?.files ?? []).map((f) => [f.path, f.size]));
  const downloads = (m?.downloads ?? []).map((path) => ({ path, size: sizes.get(path) ?? 0 }));
  const total = downloads.reduce((a, d) => a + d.size, 0);
  const recipients = (access.data ?? []).filter((g) => g.role === 'client' || g.role === 'partner');
  const publish = useMutation({
    mutationFn: () =>
      api.publish(project.id, {
        revision: form.revision,
        ...(form.title.trim() ? { title: form.title.trim() } : {}),
        ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
        ...(revPages.length ? { pages } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['publications', project.id] });
      void qc.invalidateQueries({ queryKey: ['revisions', project.id] });
      void qc.invalidateQueries({ queryKey: ['project'] });
      onClose();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    publish.mutate();
  };
  const label = rev.data?.label ?? revs.data?.find((r) => r.id === form.revision)?.label ?? '';
  const replaces = latest && latest.revisionId !== form.revision ? latest : null;

  return (
    <Dialog
      title={t('deliverTitle')}
      wide
      onClose={onClose}
      footer={
        <>
          {replaces && (
            <span className="dlg-foot-note">
              {fill(t('replacesCurrent'), {
                title: `${replaces.title || replaces.revisionLabel} (${replaces.revisionLabel})`,
              })}
            </span>
          )}
          <button className="quiet" type="button" onClick={onClose}>
            {t('cancel')}
          </button>
          <button
            className="primary"
            type="submit"
            form="deliver-form"
            disabled={!form.revision || publish.isPending}
          >
            <Icon.Send />
            {t('deliver')}
          </button>
        </>
      }
    >
      <div className="deliver-grid">
        <form id="deliver-form" className="deliver-form" onSubmit={submit}>
          <label>
            {t('frozenState')}
            <select
              required
              value={form.revision}
              onChange={(e) => setForm({ ...form, revision: e.target.value })}
            >
              {(revs.data ?? []).map((r) => {
                const c = (r.manifest as { checks?: Checks | null }).checks;
                return (
                  <option key={r.id} value={r.id}>
                    {r.label}
                    {c?.count != null ? ` · ${c.passed ? c.count : '!'}/${c.count}` : ''}
                    {activeRevs.has(r.id) ? ` · ${t('delivered').toLowerCase()}` : ''}
                  </option>
                );
              })}
            </select>
          </label>
          <label>
            {t('title')}
            <input
              value={form.title}
              placeholder={label}
              maxLength={200}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
            <span className="hint">{t('titleHint')}</span>
          </label>
          <label>
            {t('notes')}
            <textarea
              rows={4}
              maxLength={5000}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </label>
          <p className="hint">{t('immutableHint')}</p>
          <ErrorNote error={publish.error ?? rev.error} />
        </form>
        <div className="transmittal">
          <div className="group-label">{t('whatClientGets')}</div>
          <div className="transmittal-sheet">
            <dl className="transmittal-head">
              <div>
                <dt>{t('to')}</dt>
                <dd>{recipients.length ? recipients.map((g) => g.label).join(', ') : '—'}</dd>
              </div>
              <div>
                <dt>{t('project')}</dt>
                <dd>{project.name}</dd>
              </div>
              <div>
                <dt>{t('delivery')}</dt>
                <dd>{label || '—'}</dd>
              </div>
            </dl>
            {rev.isPending && form.revision ? (
              <p className="transmittal-sec muted">{t('loading')}</p>
            ) : (
              <>
                <div className="transmittal-sec">
                  <h4>{t('model')}</h4>
                  <div className="line-item">
                    <Icon.Model width={17} height={17} />
                    <span className="link-name">{m?.viewer ? t('modelInBrowser') : t('noModel')}</span>
                    {m?.viewer && (
                      <span className="tag ok">
                        <Icon.Check width={14} height={14} />
                      </span>
                    )}
                  </div>
                </div>
                {revPages.length > 0 && (
                  <fieldset className="transmittal-sec">
                    <legend>
                      <h4>
                        {t('reports')} <span>{`${pages.length}/${revPages.length}`}</span>
                      </h4>
                    </legend>
                    {revPages.map((p) => (
                      <label key={p.id} className={`check${p.audience !== 'client' ? ' off' : ''}`}>
                        <input
                          type="checkbox"
                          disabled={p.audience !== 'client'}
                          checked={pages.includes(p.id)}
                          onChange={(e) =>
                            setPages(e.target.checked ? [...pages, p.id] : pages.filter((x) => x !== p.id))
                          }
                        />
                        {p.title}
                        {p.audience !== 'client' && (
                          <span className="internal-mark">
                            <Icon.Lock width={13} height={13} />
                            {t('internal')}
                          </span>
                        )}
                      </label>
                    ))}
                  </fieldset>
                )}
                {downloads.length > 0 && (
                  <div className="transmittal-sec">
                    <h4>
                      {t('downloads')} <span>{formatBytes(total, lang)}</span>
                    </h4>
                    {downloads.map((d) => (
                      <div key={d.path} className="line-item">
                        <Icon.File width={17} height={17} />
                        <span className="link-name">{d.path.slice(d.path.lastIndexOf('/') + 1)}</span>
                        <span className="link-size">{formatBytes(d.size, lang)}</span>
                      </div>
                    ))}
                  </div>
                )}
                <p className="transmittal-sec faint small">{m?.basis || t('basisNote')}</p>
              </>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
}

/** Pages in progress (current versions), for Mynstra and reviewers: links to their draft view. */
function DraftPages({ project }: { project: Project }) {
  const { t } = useI18n();
  const drafts = useQuery({
    queryKey: ['page-session', project.id, 'draft'],
    queryFn: () => api.pageSession(project.id, 'draft'),
    staleTime: 60_000,
  });
  const pages = drafts.data?.pages ?? [];
  if (!pages.length) return null;
  return (
    <section className="section">
      <h2 className="section-title">{t('draftPreview')}</h2>
      <ul className="link-list framed">
        {pages.map((pg) => (
          <li key={pg.id}>
            <Link to={new URL(pg.url).pathname}>
              <Icon.Report width={17} height={17} />
              <span className="link-name">{pg.title}</span>
              <span className="faint mono small hide-sm">{pg.root}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
